import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Box,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { Button } from '../core';
import './HeaderModern.css';

/**
 * Header moderno seguindo a identidade visual Mexx
 * Interface clean e profissional para o sistema
 */
const HeaderModern = ({
  title = 'Mexx',
  user = null,
  onMenuToggle,
  onLogout,
  notificationCount = 0,
  onNotificationClick,
  searchValue = '',
  onSearchChange,
  onSearchSubmit,
  className = '',
  ...props
}) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationMenuAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    if (onLogout) {
      onLogout();
    }
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onSearchSubmit) {
        onSearchSubmit(searchValue);
      }
    }
  };

  return (
    <AppBar
      position="fixed"
      className={`mexx-header ${className}`}
      elevation={1}
      {...props}
    >
      <Toolbar className="mexx-header__toolbar">
        {/* Menu Toggle */}
        <IconButton
          edge="start"
          className="mexx-header__menu-button"
          onClick={onMenuToggle}
          aria-label="Abrir menu"
        >
          <MenuIcon />
        </IconButton>

        {/* Logo e Título */}
        <div className="mexx-header__brand">
          <div className="mexx-header__logo">
            <span className="mexx-header__logo-text">
              Me<span className="mexx-header__logo-accent">xx</span>
            </span>
          </div>
          {title && title !== 'Mexx' && (
            <Typography
              variant="h6"
              className="mexx-header__title"
              noWrap
            >
              {title}
            </Typography>
          )}
        </div>

        {/* Search Bar Central */}
        <div className="mexx-header__search">
          <div className="mexx-header__search-wrapper">
            <SearchIcon className="mexx-header__search-icon" />
            <InputBase
              placeholder="Buscar em tudo..."
              value={searchValue}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="mexx-header__search-input"
              inputProps={{ 'aria-label': 'buscar' }}
            />
          </div>
        </div>

        {/* Actions à direita */}
        <div className="mexx-header__actions">
          {/* Notificações */}
          <Tooltip title="Notificações">
            <IconButton
              className="mexx-header__action-button"
              onClick={handleNotificationMenuOpen}
              aria-label={`${notificationCount} notificações`}
            >
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Configurações */}
          <Tooltip title="Configurações">
            <IconButton
              className="mexx-header__action-button"
              aria-label="configurações"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          {user && (
            <div className="mexx-header__user">
              <Button
                variant="ghost"
                className="mexx-header__user-button"
                onClick={handleUserMenuOpen}
                startIcon={
                  <Avatar
                    src={user.avatar}
                    alt={user.name}
                    className="mexx-header__avatar"
                  >
                    {user.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                }
              >
                <div className="mexx-header__user-info">
                  <span className="mexx-header__user-name">{user.name}</span>
                  <span className="mexx-header__user-role">{user.role}</span>
                </div>
              </Button>
            </div>
          )}
        </div>

        {/* Menu de Notificações */}
        <Menu
          anchorEl={notificationMenuAnchor}
          open={Boolean(notificationMenuAnchor)}
          onClose={handleNotificationMenuClose}
          className="mexx-header__notification-menu"
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <div className="mexx-header__notification-header">
            <Typography variant="h6">Notificações</Typography>
            <Button variant="ghost" size="small">
              Marcar todas como lidas
            </Button>
          </div>
          <Divider />

          {notificationCount === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                Nenhuma notificação nova
              </Typography>
            </MenuItem>
          ) : (
            <>
              <MenuItem onClick={handleNotificationMenuClose}>
                <div className="mexx-header__notification-item">
                  <Typography variant="body2">
                    Novo ticket #1234 criado
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    2 minutos atrás
                  </Typography>
                </div>
              </MenuItem>
              <MenuItem onClick={handleNotificationMenuClose}>
                <div className="mexx-header__notification-item">
                  <Typography variant="body2">
                    Chat em andamento necessita atenção
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    5 minutos atrás
                  </Typography>
                </div>
              </MenuItem>
            </>
          )}

          <Divider />
          <MenuItem onClick={handleNotificationMenuClose}>
            <Button fullWidth variant="ghost">
              Ver todas as notificações
            </Button>
          </MenuItem>
        </Menu>

        {/* Menu do Usuário */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          className="mexx-header__user-menu"
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {user && (
            <>
              <div className="mexx-header__user-menu-header">
                <Avatar
                  src={user.avatar}
                  alt={user.name}
                  className="mexx-header__user-menu-avatar"
                >
                  {user.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <Typography variant="subtitle1">{user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.email}
                  </Typography>
                </div>
              </div>
              <Divider />
            </>
          )}

          <MenuItem onClick={handleUserMenuClose}>
            <AccountCircleIcon className="mexx-header__menu-icon" />
            <Typography variant="inherit">Meu Perfil</Typography>
          </MenuItem>

          <MenuItem onClick={handleUserMenuClose}>
            <SettingsIcon className="mexx-header__menu-icon" />
            <Typography variant="inherit">Configurações</Typography>
          </MenuItem>

          <MenuItem onClick={handleUserMenuClose}>
            <LanguageIcon className="mexx-header__menu-icon" />
            <Typography variant="inherit">Idioma</Typography>
          </MenuItem>

          <MenuItem onClick={handleUserMenuClose}>
            <DarkModeIcon className="mexx-header__menu-icon" />
            <Typography variant="inherit">Tema Escuro</Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout}>
            <LogoutIcon className="mexx-header__menu-icon" />
            <Typography variant="inherit">Sair</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

HeaderModern.propTypes = {
  title: PropTypes.string,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    avatar: PropTypes.string
  }),
  onMenuToggle: PropTypes.func,
  onLogout: PropTypes.func,
  notificationCount: PropTypes.number,
  onNotificationClick: PropTypes.func,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  className: PropTypes.string
};

export default HeaderModern;